import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, imgSrc, errMsg, prepareImage } from "../../lib/api";
import Uploader from "./Uploader";

export default function BioCard({ settings, onSaved }) {
  const [form, setForm] = useState({ artist_name: "", bio: "", contact_email: "", sms_email: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm((f) => {
      const untouched =
        f.artist_name === (settings.artist_name || "") &&
        f.bio === (settings.bio || "") &&
        f.contact_email === (settings.contact_email || "") &&
        f.sms_email === (settings.sms_email || "");
      return untouched
        ? {
            artist_name: settings.artist_name || "",
            bio: settings.bio || "",
            contact_email: settings.contact_email || "",
            sms_email: settings.sms_email || "",
          }
        : f;
    });
  }, [settings]);

  const portrait = settings?.portrait_path || "";

  const pickPortrait = async (f) => {
    if (!f || uploading) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Choose an image file");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Image is larger than 25 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await prepareImage(f));
      await api.post("/settings/portrait", fd);
      toast.success("Portrait updated — it's live in the gallery");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const removePortrait = async () => {
    if (uploading || !portrait) return;
    setUploading(true);
    try {
      await api.delete("/settings/portrait");
      toast.success("Portrait removed — the About section now shows your words alone");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.put("/settings", form);
      toast.success("Saved — the gallery is updated");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="bio-form" className="border border-line bg-panel p-6 md:p-8">
      <p className="eyebrow !text-ochre/90">About You</p>
      <h2 className="font-display text-2xl mt-2">Your bio & alerts</h2>

      <div className="mt-6">
        <Uploader preview={portrait ? imgSrc(portrait) : null} onPick={pickPortrait} onClear={removePortrait} />
        <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute mt-3">
          {portrait
            ? "Your portrait appears beside your bio — drop a new photo to replace it, or remove it"
            : "Add a portrait photo of yourself (optional) — it shows beside your bio"}
        </p>
      </div>

      <div className="mt-5 space-y-5">
        <div>
          <label className="field-label" htmlFor="bio-name">Artist name</label>
          <input
            id="bio-name"
            value={form.artist_name}
            onChange={(e) => setForm((f) => ({ ...f, artist_name: e.target.value }))}
            placeholder="Your name"
            className="field-input"
            data-testid="bio-name-input"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="bio-email">Contact email — receives enquiries & sale alerts</label>
          <input
            id="bio-email"
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
            placeholder="you@example.com"
            className="field-input"
            data-testid="bio-email-input"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="bio-sms">Text alerts — phone gateway</label>
          <input
            id="bio-sms"
            value={form.sms_email}
            onChange={(e) => setForm((f) => ({ ...f, sms_email: e.target.value }))}
            placeholder="3104901250@vtext.com"
            className="field-input"
            data-testid="bio-sms-input"
          />
          <p className="font-meta text-[9px] uppercase tracking-[0.2em] text-mute mt-2">
            Verizon: number@vtext.com · AT&T: number@txt.att.net · T-Mobile: number@tmomail.net — leave empty for no texts
          </p>
        </div>
        <div>
          <label className="field-label" htmlFor="bio-text">Bio</label>
          <textarea
            id="bio-text"
            rows={6}
            value={form.bio}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
            placeholder="Who you are, how you make your work, what it means…"
            className="field-input resize-y leading-relaxed"
            data-testid="bio-text-input"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="bio-save-button"
        className="btn-metal mt-8 w-full font-meta text-[11px] uppercase tracking-[0.3em] py-4 text-ink"
      >
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
}
